import { Component, Input, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';

Chart.register(...registerables);

@Component({
  selector: 'app-percentile-chart',
  templateUrl: './percentile-chart.component.html',
  styleUrls: ['./percentile-chart.component.css'],
  standalone: false
})
export class PercentileChartComponent implements OnInit, AfterViewInit {
  @Input() percentileInfo: any;
  @ViewChild('barCanvas') barCanvas!: ElementRef<HTMLCanvasElement>;

  private chart?: Chart;

  overallPercentile: number = 0;
  percentileCategory: string = '';
  strengths: string[] = [];
  weaknesses: string[] = [];

  constructor() { }

  ngOnInit(): void {
    if (this.percentileInfo) {
      this.overallPercentile = this.percentileInfo.overall_percentile || 0;
      this.percentileCategory = this.percentileInfo.percentile_category || 'N/A';
      this.strengths = this.percentileInfo.strengths || [];
      this.weaknesses = this.percentileInfo.weaknesses || [];
    }
  }

  ngAfterViewInit(): void {
    this.setupPercentileChart();
  }

  setupPercentileChart(): void {
    if (!this.percentileInfo || !this.percentileInfo.trait_percentiles || !this.barCanvas) return;

    const traits = Object.keys(this.percentileInfo.trait_percentiles);
    const percentiles = Object.values(this.percentileInfo.trait_percentiles) as number[];

    // Sort by percentile for better visualization
    const combined = traits.map((trait, index) => ({
      label: this.formatLabel(trait),
      value: percentiles[index]
    }));

    combined.sort((a, b) => b.value - a.value);

    const labels = combined.map(item => item.label);
    const data = combined.map(item => item.value);
    const backgroundColors = combined.map(item => {
      if (item.value >= 75) return 'rgba(76, 175, 80, 0.7)';
      if (item.value >= 50) return 'rgba(33, 150, 243, 0.7)';
      if (item.value >= 25) return 'rgba(255, 193, 7, 0.7)';
      return 'rgba(244, 67, 54, 0.7)';
    });
    const borderColors = combined.map(item => {
      if (item.value >= 75) return 'rgba(76, 175, 80, 1)';
      if (item.value >= 50) return 'rgba(33, 150, 243, 1)';
      if (item.value >= 25) return 'rgba(255, 193, 7, 1)';
      return 'rgba(244, 67, 54, 1)';
    });

    const config: ChartConfiguration = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2,
            borderRadius: 6
          }
        ]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            min: 0,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          y: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                size: 11
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            callbacks: {
              label: function(context) {
                return context.parsed.x + 'th percentile';
              }
            }
          }
        }
      }
    };

    this.chart = new Chart(this.barCanvas.nativeElement, config);
  }

  formatLabel(key: string): string {
    return key.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  getPercentileColor(): string {
    if (this.overallPercentile >= 75) return '#4caf50';
    if (this.overallPercentile >= 50) return '#2196f3';
    if (this.overallPercentile >= 25) return '#ffc107';
    return '#f44336';
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}